import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, StoryDetail, StoryPhase, StoryObjective } from '../services/adminApi'
import charStyles from './CharactersPage.module.css'
import styles from './StoryDetailPage.module.css'

interface Props {
  filename: string
}

export default function StoryDetailPage({ filename }: Props) {
  const { apiKey } = useAdmin()

  const [story, setStory] = useState<StoryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const api = createAdminApi(() => apiKey)
    setLoading(true)
    setError('')
    api.getStory(filename)
      .then(setStory)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey, filename])

  return (
    <div className={charStyles.detailPage}>
      <header className={charStyles.detailHeader}>
        <a href="#stories" className={charStyles.back}>← Stories</a>
        {story && (
          <>
            <h1>{story.title || filename}</h1>
            {story.version && (
              <span className={charStyles.versionBadge}>v{story.version}</span>
            )}
          </>
        )}
      </header>

      <div className={charStyles.detailContent}>
        {loading && <p className={charStyles.empty}>Loading…</p>}
        {error && <p className={charStyles.error}>{error}</p>}

        {story && (
          <>
            <div className={charStyles.section}>
              <p className={charStyles.sectionTitle}>Story</p>
              <div className={charStyles.fieldRow}>
                <span className={charStyles.fieldLabel}>ID</span>
                <span className={charStyles.fieldMono}>{story.id}</span>
              </div>
              {story.description && (
                <div className={charStyles.fieldRow}>
                  <span className={charStyles.fieldLabel}>Description</span>
                  <span className={charStyles.fieldValue}>{story.description}</span>
                </div>
              )}
              <div className={charStyles.fieldRow}>
                <span className={charStyles.fieldLabel}>Character</span>
                <span className={charStyles.fieldValue}>{story.character.name}</span>
              </div>
              <div className={charStyles.fieldRow}>
                <span className={charStyles.fieldLabel}>Start Phase</span>
                <span className={charStyles.fieldMono}>{story.startPhase}</span>
              </div>
            </div>

            {(story.globalFacts.public.length > 0 || story.globalFacts.private.length > 0) && (
              <div className={charStyles.section}>
                <p className={charStyles.sectionTitle}>Global Facts</p>
                <FactsBlock facts={story.globalFacts} />
              </div>
            )}

            <div className={charStyles.section}>
              <p className={charStyles.sectionTitle}>
                Phases ({Object.keys(story.phases).length})
              </p>
              <div className={styles.phasesGrid}>
                {Object.entries(story.phases).sort(([a], [b]) => {
                  if (a === story.startPhase) return -1
                  if (b === story.startPhase) return 1
                  return a.localeCompare(b)
                }).map(([key, phase]) => (
                  <PhaseBox key={key} phaseKey={key} phase={phase} isStart={key === story.startPhase} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function PhaseBox({ phaseKey, phase, isStart }: { phaseKey: string; phase: StoryPhase; isStart: boolean }) {
  if (!phase) return null

  const hasFacts = phase.facts.public?.length > 0 || phase.facts.private?.length > 0

  return (
    <div className={styles.phaseBox} data-start={isStart}>
      <div className={styles.phaseHeader}>
        <span className={styles.phaseName}>{phaseKey}</span>
        {isStart && <span className={styles.startBadge}>start</span>}
        {phase.tone && <span className={styles.toneBadge}>{phase.tone}</span>}
        {phase.maxTurns > 0 && <span className={styles.turnsBadge}>max {phase.maxTurns} turns</span>}
      </div>

      {phase.description && (
        <p className={styles.phaseDescription}>{phase.description}</p>
      )}

      <div className={styles.phaseBody}>
        {phase.objectives.length > 0 && (
          <div>
            <span className={styles.subsectionTitle}>Objectives</span>
            <div className={styles.objectivesList}>
              {phase.objectives.map(obj => (
                <ObjectiveCard key={obj.id} obj={obj} />
              ))}
            </div>
          </div>
        )}

        {hasFacts && (
          <div>
            <span className={styles.subsectionTitle}>Facts</span>
            <FactsBlock facts={phase.facts} />
          </div>
        )}

        {phase.triggers.length > 0 && (
          <div>
            <span className={styles.subsectionTitle}>Triggers</span>
            <div className={styles.triggersList}>
              {phase.triggers
                .slice()
                .sort((a, b) => a.priority - b.priority)
                .map(t => (
                  <div key={t.id} className={styles.prefab}>
                    <span className={styles.prefabGoto}>{t.goto}</span>
                    <span>{t.condition}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.phaseFooter}>
        {phase.onComplete
          ? <span className={styles.transitionComplete}>{phase.onComplete}</span>
          : <span className={styles.noTransition}>no continuation</span>
        }
        {phase.onFailure && (
          <span className={styles.transitionFailure}>{phase.onFailure}</span>
        )}
      </div>
    </div>
  )
}

function ObjectiveCard({ obj }: { obj: StoryObjective }) {
  return (
    <div className={styles.objective}>
      <div className={styles.objectiveHeader}>
        <span className={styles.objectiveId}>{obj.id}</span>
        <span className={styles.completionBadge}>{obj.completionType}</span>
        {obj.urgency && <span className={styles.completionBadge}>{obj.urgency}</span>}
      </div>
      <p className={styles.objectiveDesc}>{obj.description}</p>
      {(obj.condition || obj.choice) && (
        <p className={styles.objectiveCondition}>{obj.condition || obj.choice}</p>
      )}
      {obj.tactics && obj.tactics.length > 0 && (
        <ul className={styles.objectiveTactics}>
          {obj.tactics.map((t, i) => (
            <li key={i} className={styles.objectiveTactic}>{t}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FactsBlock({ facts }: { facts: { public: string[]; private: string[] } }) {
  return (
    <div className={styles.factsFull}>
      {facts.public?.length > 0 && (
        <div className={styles.factsGroup}>
          <span className={styles.factsLabel}>Public</span>
          {facts.public?.map((f, i) => (
            <span key={i} className={styles.factItem}>{f}</span>
          ))}
        </div>
      )}
      {facts.private?.length > 0 && (
        <div className={styles.factsGroup}>
          <span className={styles.factsLabel}>Private</span>
          {facts.private?.map((f, i) => (
            <span key={i} className={styles.factItem}>{f}</span>
          ))}
        </div>
      )}
    </div>
  )
}
